/**
 * Level Up Modal
 * Celebration modal with confetti animation - ported from mobile
 */

import { useEffect, useState, useMemo } from 'react';
import { Medal, Ribbon, Star, Trophy, Diamond, Gift, X } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { LevelUpData } from '../types';
import { getLevelTierColors } from '../utils/helpers';
import { CONFETTI_COLORS, CONFETTI_COUNT } from '../utils/constants';

interface LevelUpModalProps {
  isOpen: boolean;
  levelUpData: LevelUpData | null;
  onClose: () => void;
}

// Static icon components - defined outside component
const LEVEL_ICONS = {
  medal: Medal,
  ribbon: Ribbon,
  star: Star,
  trophy: Trophy,
  diamond: Diamond,
} as const;

// Get icon key based on level
function getLevelIconKey(level: number): keyof typeof LEVEL_ICONS {
  if (level <= 2) return 'medal';
  if (level <= 4) return 'ribbon';
  if (level <= 6) return 'star';
  if (level <= 8) return 'trophy';
  return 'diamond';
}

// Confetti particle component
function ConfettiParticle({
  index,
  color,
}: {
  index: number;
  color: string;
}) {
  const [style, setStyle] = useState({
    left: '50%',
    top: '50%',
    opacity: 0,
    transform: 'translate(-50%, -50%) rotate(0deg)',
  });

  useEffect(() => {
    const angle = (index / CONFETTI_COUNT) * 360;
    const distance = 100 + Math.random() * 100;
    const duration = 1 + Math.random() * 0.5;
    const delay = index * 0.05;

    const x = Math.cos((angle * Math.PI) / 180) * distance;
    const y = Math.sin((angle * Math.PI) / 180) * distance;
    const rotation = Math.random() * 720 - 360;

    setTimeout(() => {
      setStyle({
        left: `calc(50% + ${x}px)`,
        top: `calc(50% + ${y}px)`,
        opacity: 1,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
      });
    }, delay * 1000);

    setTimeout(() => {
      setStyle((prev) => ({ ...prev, opacity: 0 }));
    }, (delay + duration) * 1000);
  }, [index]);

  const shapes = ['rounded-sm', 'rounded-full', ''];
  const shape = shapes[index % shapes.length];

  return (
    <div
      className={`absolute w-3 h-3 ${shape} transition-all pointer-events-none`}
      style={{
        ...style,
        backgroundColor: color,
        transitionDuration: '1s',
        transitionTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}
    />
  );
}

export function LevelUpModal({ isOpen, levelUpData, onClose }: LevelUpModalProps) {
  const { t } = useLanguage();
  const [showContent, setShowContent] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Handle keyboard events at document level
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to defer state updates and avoid synchronous setState warning
      const rafId = requestAnimationFrame(() => {
        setShowConfetti(true);
      });
      const timer = setTimeout(() => setShowContent(true), 200);
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timer);
      };
    } else {
      // Also defer the reset to avoid synchronous setState in effect
      const rafId = requestAnimationFrame(() => {
        setShowContent(false);
        setShowConfetti(false);
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isOpen]);

  // Memoize level-dependent values
  const iconKey = useMemo(() => levelUpData ? getLevelIconKey(levelUpData.newLevel) : 'medal', [levelUpData]);
  const LevelIcon = LEVEL_ICONS[iconKey];

  if (!isOpen || !levelUpData) return null;

  const { newLevel, title, tier, rewards } = levelUpData;
  const tierColors = getLevelTierColors(tier);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity border-0 cursor-default"
        onClick={onClose}
        aria-label={t('common.close', 'Close')}
      />

      {/* Modal */}
      <div
        className={`relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden transition-all duration-500 ${
          showContent ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
        }`}
      >
        {/* Confetti Container */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {showConfetti &&
            Array.from({ length: CONFETTI_COUNT }).map((_, i) => (
              <ConfettiParticle
                key={i}
                index={i}
                color={CONFETTI_COLORS[i % CONFETTI_COLORS.length]}
              />
            ))}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Animated badge */}
          <div className="relative mb-6">
            {/* Glow effect */}
            <div
              className={`absolute inset-0 rounded-full bg-gradient-to-br ${tierColors.gradient} blur-2xl opacity-40 animate-pulse`}
              style={{ transform: 'scale(1.5)' }}
            />

            {/* Main badge */}
            <div
              className={`relative w-32 h-32 mx-auto rounded-full bg-gradient-to-br ${tierColors.gradient} flex items-center justify-center shadow-xl animate-bounce`}
              style={{ animationDuration: '2s' }}
            >
              <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center">
                <LevelIcon className={`w-16 h-16 ${tierColors.text}`} />
              </div>

              {/* Level number */}
              <div
                className={`absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br ${tierColors.gradient} flex items-center justify-center text-white font-bold text-xl shadow-lg border-4 border-white`}
              >
                {newLevel}
              </div>
            </div>
          </div>

          {/* Celebration text */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('challenges.levelUp.congrats', 'Congratulations!')}
            </h2>
            <p className="text-gray-600">
              {t('challenges.levelUp.reached', "You've reached")}
            </p>
            <p className={`text-3xl font-bold ${tierColors.text} mt-2`}>
              {t('challenges.levelUp.level', 'Level')} {newLevel}: {title}
            </p>
          </div>

          {/* Rewards */}
          {rewards.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-3">
                <Gift className="w-4 h-4" />
                <span>{t('challenges.levelUp.rewards', 'Your rewards:')}</span>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {rewards.map((reward, index) => (
                  <span
                    key={index}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${tierColors.text}`}
                    style={{
                      backgroundColor: tier === 'gold' ? '#fef3c7' :
                                       tier === 'silver' ? '#f3f4f6' :
                                       tier === 'bronze' ? '#fef3c7' :
                                       tier === 'platinum' ? '#f3e8ff' : '#ecfeff'
                    }}
                  >
                    {reward}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action button */}
          <button
            onClick={onClose}
            className={`w-full py-4 rounded-xl text-white font-semibold text-lg bg-gradient-to-r ${tierColors.gradient} hover:opacity-90 transition-opacity shadow-lg`}
          >
            {t('challenges.levelUp.awesome', 'Awesome!')}
          </button>
        </div>
      </div>
    </div>
  );
}
